import type { ActionConfig } from "./types";

/** Give cancellation ownership of the UI opened by this invocation. */
export function invokeActionWithLifecycle<
  A extends ActionConfig<any, any, any, any>,
>(action: A, context: Parameters<A["onInvoke"]>[0]) {
  const { openComponent, openOverlay, cancel, setLoading } = context;
  const closeHandlers = new Set<() => void>();
  let successCount = 0;
  const openActionComponent: typeof openComponent = (state, options) => {
    // Saving an earlier step does not complete an editor opened afterward.
    const successCountAtOpen = successCount;
    const component = openComponent(state, {
      ...options,
      onClose: () => {
        const closedExternally = closeHandlers.delete(closeComponent);
        options?.onClose?.();
        if (closedExternally && successCount === successCountAtOpen)
          cancelAction();
      },
    });
    const closeComponent: typeof component.closeComponent = (closeOptions) => {
      if (!closeHandlers.has(closeComponent)) return;
      if (closeOptions?.skipDirtyCheck !== false) {
        closeHandlers.delete(closeComponent);
      }
      component.closeComponent(closeOptions);
    };
    closeHandlers.add(closeComponent);
    return { closeComponent };
  };
  const openActionOverlay: typeof openOverlay = (render) =>
    openOverlay(({ close }) => {
      const closeOverlay = () => {
        if (!closeHandlers.delete(closeOverlay)) return;
        close();
      };
      closeHandlers.add(closeOverlay);
      return render({ close: closeOverlay });
    });
  let cancelled = false;
  const cancelAction = () => {
    if (cancelled) return;
    cancelled = true;
    for (const close of closeHandlers) close();
    setLoading(false);
    cancel();
  };
  return action.onInvoke({
    ...context,
    openComponent: openActionComponent,
    openOverlay: openActionOverlay,
    cancel: cancelAction,
    success: (result) => {
      if (!cancelled) {
        successCount += 1;
        context.success(result);
      }
    },
    fail: (error) => {
      if (!cancelled) context.fail(error);
    },
  });
}
